import { useMemo } from "react";
import { IUser } from "../../../../api/homesites/homesites.types";
import { Community } from "../../../../api/communities/types";
import { HomeSitesDataIndex } from "../../../../reactQuery/homeSitesDataIndex/types";

interface UseBulkAssignDataProps {
     homesites: HomeSitesDataIndex[];
     communities?: Community[];
     conciergesWithSelf: IUser[];
     userInfo: any;
     selectedBulkConcierge: IUser | null;
     selectedBulkCommunity: string;
}

export const useBulkAssignData = ({
     homesites,
     communities = [],
     conciergesWithSelf,
     userInfo,
     selectedBulkConcierge,
     selectedBulkCommunity,
}: UseBulkAssignDataProps) => {
     // Get current user as concierge option
     const currentUserAsConcierge = useMemo(() => {
          if (!userInfo?.email || !userInfo?.name) return null;
          return conciergesWithSelf.find((c) => c.email === userInfo.email) || null;
     }, [userInfo?.email, userInfo?.name, conciergesWithSelf]);

     // Sort concierges with current user first
     const sortedConcierges = useMemo(() => {
          if (!currentUserAsConcierge) return conciergesWithSelf.filter((c) => c.name !== "");

          const otherConcierges = conciergesWithSelf.filter(
               (c) => c.name !== "" && c.email !== currentUserAsConcierge.email
          );

          // Sort other concierges alphabetically
          const sortedOthers = otherConcierges.sort((a, b) => a.name.localeCompare(b.name));

          // Put current user first
          return [currentUserAsConcierge, ...sortedOthers];
     }, [conciergesWithSelf, currentUserAsConcierge]);

     // Get unique list of communities from homesites
     const availableCommunities = useMemo(() => {
          const communitySet = new Set<string>();
          homesites?.forEach((hs) => {
               if (hs.communityName) {
                    communitySet.add(hs.communityName);
               }
          });
          return Array.from(communitySet).sort();
     }, [homesites]);

     // Filter homesites by selected community
     const filterByCommunity = (hs: HomeSitesDataIndex) => hs.communityName === selectedBulkCommunity;

     // Calculate total homesites in selected community
     const totalHomesitesInCommunity = useMemo(() => {
          if (!selectedBulkCommunity) return 0;
          return homesites?.filter(filterByCommunity).length || 0;
     }, [homesites, selectedBulkCommunity]);

     // Calculate number of homesites that will be assigned based on selected filters
     const homesitesToUpdateCount = useMemo(() => {
          if (!selectedBulkCommunity || !selectedBulkConcierge) return 0;

          const filteredByCommunity = homesites?.filter(filterByCommunity) || [];

          if (filteredByCommunity.length === 0) return 0;

          // Only include homesites that are:
          // 1. Not from Salesforce
          // 2. Not already assigned to the selected concierge (identify by email)
          // 3. Not already assigned to any other concierge (only unassigned homesites)
          const selectedEmailNorm = (selectedBulkConcierge.email || "").trim().toLowerCase();
          return filteredByCommunity.filter(
               (hs) =>
                    hs.conciergeSource !== "SF Opportunity" &&
                    !(hs.conciergeEmail && (hs.conciergeEmail || "").trim().toLowerCase() === selectedEmailNorm) &&
                    !(hs.conciergeName && hs.conciergeEmail) // Exclude homesites assigned to other concierges
          ).length;
     }, [homesites, selectedBulkCommunity, selectedBulkConcierge]);

     // Check if there are homesites that can be updated
     const hasHomesitesToUpdate = useMemo(() => {
          if (!selectedBulkCommunity || !selectedBulkConcierge) return false;

          const filteredByCommunity = homesites?.filter(filterByCommunity) || [];

          if (filteredByCommunity.length === 0) return false;

          // Only include homesites that are:
          // 1. Not from Salesforce
          // 2. Not already assigned to the selected concierge (identify by email)
          // 3. Not already assigned to any other concierge (only unassigned homesites)
          const selectedEmailNorm = (selectedBulkConcierge.email || "").trim().toLowerCase();
          const assignableHomesites = filteredByCommunity.filter(
               (hs) =>
                    hs.conciergeSource !== "SF Opportunity" &&
                    !(hs.conciergeEmail && (hs.conciergeEmail || "").trim().toLowerCase() === selectedEmailNorm) &&
                    !(hs.conciergeName && hs.conciergeEmail) // Exclude homesites assigned to other concierges
          );

          return assignableHomesites.length > 0;
     }, [homesites, selectedBulkCommunity, selectedBulkConcierge]);

     // Get concierge assignments breakdown for selected community.
     // Identify by email (normalized) for calculation so the same person shows once even if name differs (e.g. "X" vs "X (Contractor)").
     const communityConciergeBreakdown = useMemo(() => {
          if (!selectedBulkCommunity) return null;

          const filteredByCommunity = homesites?.filter(filterByCommunity) || [];

          if (filteredByCommunity.length === 0) return null;

          const conciergeMap = new Map<string, { name: string; email: string; count: number }>();
          const salesforceMap = new Map<string, { name: string; email: string; count: number }>();
          let unassignedCount = 0;
          let salesforceCount = 0;

          filteredByCommunity.forEach((hs) => {
               if (hs.conciergeSource === "SF Opportunity") {
                    salesforceCount++;
                    // Aggregate Salesforce-assigned by concierge for display (sub-bullets)
                    if (hs.conciergeName || hs.conciergeEmail) {
                         const emailKey = (hs.conciergeEmail || "").trim().toLowerCase() || `name:${(hs.conciergeName || "").trim()}`;
                         const current = salesforceMap.get(emailKey);
                         if (current) {
                              current.count++;
                         } else {
                              salesforceMap.set(emailKey, {
                                   name: hs.conciergeName || "—",
                                   email: (hs.conciergeEmail || "").trim(),
                                   count: 1,
                              });
                         }
                    }
               } else if (hs.conciergeName && hs.conciergeEmail) {
                    const emailKey = (hs.conciergeEmail || "").trim().toLowerCase();
                    const current = conciergeMap.get(emailKey);
                    if (current) {
                         current.count++;
                    } else {
                         conciergeMap.set(emailKey, {
                              name: hs.conciergeName,
                              email: (hs.conciergeEmail || "").trim(),
                              count: 1,
                         });
                    }
               } else {
                    unassignedCount++;
               }
          });

          // Use canonical display name from conciergesWithSelf when available (avoids "X" vs "X (Contractor)" duplicates)
          const concierges = Array.from(conciergeMap.values())
               .map((entry) => {
                    const canonical = conciergesWithSelf.find(
                         (c) => (c.email || "").trim().toLowerCase() === (entry.email || "").trim().toLowerCase()
                    );
                    return {
                         name: canonical?.name ?? entry.name,
                         email: entry.email,
                         count: entry.count,
                    };
               })
               .sort((a, b) => b.count - a.count);
          const hasMismatch = concierges.length > 1;
          // Community dataset: if this community has conciergeName/conciergeEmail, use for "Community assigned" vs "Homesite assigned" only
          const communityFromDataset = communities.find((c) => c.communityName === selectedBulkCommunity);
          const communityConciergeEmailNorm = (communityFromDataset?.conciergeEmail ?? "").trim().toLowerCase();
          const communityConciergeNameFromDataset = (communityFromDataset?.conciergeName ?? "").trim();
          const hasCommunityConciergeInDataset = !!communityConciergeEmailNorm && !!communityConciergeNameFromDataset;
          const communityConciergeEmail = hasCommunityConciergeInDataset ? communityConciergeEmailNorm : null;
          const communityConciergeDisplayName = hasCommunityConciergeInDataset
               ? (conciergesWithSelf.find((c) => (c.email || "").trim().toLowerCase() === communityConciergeEmailNorm)?.name ?? communityConciergeNameFromDataset)
               : null;
          // Total assignable homesites (excluding Salesforce) — used for assign/unassign/reassign
          const totalAssignable = concierges.reduce((sum, c) => sum + c.count, 0) + unassignedCount;
          // Total including Salesforce — for display only; Salesforce is not included in assign/unassign/reassign
          const totalIncludingSalesforce = totalAssignable + salesforceCount;
          const salesforceFromMap = Array.from(salesforceMap.values()).sort((a, b) => b.count - a.count);
          const salesforceMappedCount = salesforceFromMap.reduce((sum, c) => sum + c.count, 0);
          const salesforceUnmappedCount = salesforceCount - salesforceMappedCount;
          const salesforceAssignments =
               salesforceUnmappedCount > 0
                    ? [...salesforceFromMap, { name: "—", email: "", count: salesforceUnmappedCount }]
                    : salesforceFromMap;

          // Total assigned concierges in this community (CLO + Salesforce) for display
          const totalAssignedInCommunity = concierges.length + salesforceAssignments.length;
          const singleConcierge = totalAssignedInCommunity === 1
               ? (concierges[0] ?? salesforceAssignments[0] ?? null)
               : null;
          const userEmailNorm = (userInfo?.email || "").trim().toLowerCase();
          const isSingleConciergeCurrentUser =
               !!singleConcierge &&
               !!userEmailNorm &&
               (singleConcierge.email || "").trim().toLowerCase() === userEmailNorm;

          return {
               concierges,
               unassignedCount,
               salesforceCount,
               salesforceAssignments,
               hasMismatch,
               totalConcierges: concierges.length,
               totalAssignedInCommunity,
               isSingleConciergeCurrentUser,
               communityConciergeEmail,
               communityConciergeDisplayName,
               totalAssignable,
               totalIncludingSalesforce,
          };
     }, [homesites, selectedBulkCommunity, communities, conciergesWithSelf, userInfo?.email]);

     // Get communities where the selected concierge is currently assigned with counts.
     // Identify by email (normalized) so assignments show even when stored name differs (e.g. "X" vs "X (Contractor)").
     const conciergeCommunities = useMemo(() => {
          if (!selectedBulkConcierge) return [];

          const communityMap = new Map<string, { assigned: number; available: number }>();
          const selectedEmailNorm = (selectedBulkConcierge.email || "").trim().toLowerCase();

          homesites?.forEach((hs) => {
               if (!hs.communityName || hs.conciergeSource === "SF Opportunity") return;

               const isAssigned =
                    !!hs.conciergeEmail &&
                    (hs.conciergeEmail || "").trim().toLowerCase() === selectedEmailNorm;
               const isUnassigned = !hs.conciergeName && !hs.conciergeEmail;

               const current = communityMap.get(hs.communityName) || { assigned: 0, available: 0 };

               if (isAssigned) {
                    current.assigned++;
               } else if (isUnassigned) {
                    // Only count truly unassigned homesites as "available"
                    current.available++;
               }
               // Homesites assigned to other concierges are not counted as available

               communityMap.set(hs.communityName, current);
          });

          // Only return communities where the concierge is assigned
          return Array.from(communityMap.entries())
               .filter(([_, counts]) => counts.assigned > 0)
               .map(([community, counts]) => ({ community, ...counts }))
               .sort((a, b) => a.community.localeCompare(b.community));
     }, [homesites, selectedBulkConcierge]);

     return {
          currentUserAsConcierge,
          sortedConcierges,
          availableCommunities,
          totalHomesitesInCommunity,
          homesitesToUpdateCount,
          hasHomesitesToUpdate,
          communityConciergeBreakdown,
          conciergeCommunities,
     };
};
